import { customFetch } from "@workspace/api-client-react";
import type {
  ChannelType,
  DeviceMediaItem,
  DevicePlaylist,
} from "@workspace/api-client-react";

export type MediaSource = "catalogue" | "asset";

export interface ResolveMediaParams {
  source: MediaSource;
  catalogueTrackId?: string;
  mediaAssetId?: string;
}

/** Resolve a library item to a playable DeviceMediaItem via the device API. */
export async function resolveDeviceMedia(
  params: ResolveMediaParams,
): Promise<DeviceMediaItem | null> {
  const qs = new URLSearchParams({ source: params.source });
  if (params.catalogueTrackId) qs.set("catalogueTrackId", params.catalogueTrackId);
  if (params.mediaAssetId) qs.set("mediaAssetId", params.mediaAssetId);
  try {
    return await customFetch<DeviceMediaItem>(
      `/api/device/resolve-media?${qs.toString()}`,
    );
  } catch {
    return null;
  }
}

/**
 * Resolve an arbitrary workspace playlist into a playable DevicePlaylist via the
 * device API. Used when an operator plays a library playlist that isn't part of
 * the device's schedule sync (so it isn't already in `sync.playlists`).
 */
export async function resolveDevicePlaylist(
  playlistId: string,
): Promise<DevicePlaylist | null> {
  const qs = new URLSearchParams({ playlistId });
  try {
    return await customFetch<DevicePlaylist>(
      `/api/device/resolve-playlist?${qs.toString()}`,
    );
  } catch {
    return null;
  }
}

export interface MediaRefArgs {
  playlistId?: string;
  source?: MediaSource;
  catalogueTrackId?: string;
  mediaAssetId?: string;
  channelType?: ChannelType;
}

export function parseMediaRef(args: Record<string, unknown>): MediaRefArgs | null {
  const playlistId = args["playlistId"];
  if (typeof playlistId === "string" && playlistId) {
    return { playlistId };
  }
  const source = args["source"];
  if (source !== "catalogue" && source !== "asset") return null;
  const catalogueTrackId = args["catalogueTrackId"];
  const mediaAssetId = args["mediaAssetId"];
  const channelType = args["channelType"];
  if (source === "catalogue" && typeof catalogueTrackId === "string" && catalogueTrackId) {
    return {
      source: "catalogue",
      catalogueTrackId,
      channelType: typeof channelType === "string" ? (channelType as ChannelType) : undefined,
    };
  }
  if (source === "asset" && typeof mediaAssetId === "string" && mediaAssetId) {
    return {
      source: "asset",
      mediaAssetId,
      channelType: typeof channelType === "string" ? (channelType as ChannelType) : undefined,
    };
  }
  return null;
}
